import { useEffect, useRef, useState } from 'react';
import { toast } from '../components/Toast.tsx';
import { api } from '../lib/api.ts';
import { navigate } from '../lib/router.tsx';
import { finishOAuth, useSession, verifyEmailLink } from '../lib/session.ts';
import { openAuth } from '../lib/ui.ts';

interface LinkPeek {
  email: string;
  account: string | null;
  sameDevice: boolean;
  alreadyThere: boolean;
}

/** Where the email link and Google / Apple send people back to. */
export function SignInPage() {
  const { status } = useSession();
  const [error, setError] = useState<string | null>(null);
  // An email link opened on another device: say whose account it is before signing in.
  const [confirm, setConfirm] = useState<{ link: string; peek: LinkPeek } | null>(null);
  const [busy, setBusy] = useState(false);
  const done = useRef(false);

  const go = (to: string | null) => navigate(to && to.startsWith('/') && !to.startsWith('//') ? to : '/', { replace: true });
  const finish = (p: Promise<{ me: { username: string | null }; isNew: boolean }>, to: string | null) =>
    p
      .then((r) => {
        toast(r.isNew ? `Welcome to ChessBut, ${r.me.username}!` : `Welcome back, ${r.me.username}!`);
        go(to);
      })
      .catch((err) => setError((err as Error).message));

  useEffect(() => {
    if (status !== 'ready' || done.current) return;
    done.current = true;
    const q = new URLSearchParams(location.search);
    const link = q.get('link');
    const handoff = q.get('handoff');
    const back = q.get('return');
    history.replaceState(null, '', '/signin'); // don't leave one-time codes in the address bar
    if (link) {
      api<LinkPeek>('/api/auth/email/peek', { body: { link } })
        .then((peek) => {
          if (peek.sameDevice || peek.alreadyThere) void finish(verifyEmailLink(link), '/you');
          else setConfirm({ link, peek });
        })
        .catch((err) => setError((err as Error).message));
    } else if (handoff) void finish(finishOAuth(handoff), back);
    else setError(q.get('error') === 'cancelled' ? 'Sign-in was cancelled.' : (q.get('error') ?? 'That sign-in link didn’t work.'));
  }, [status]);

  if (confirm) {
    const { peek } = confirm;
    return (
      <div className="page narrow">
        <h1 className="page-title">Sign in on this device?</h1>
        <p>
          This link signs in to {peek.account ? <b>{peek.account}</b> : 'a new account'} ({peek.email}).
        </p>
        <p className="muted">Anything you’ve played on this device as a guest moves to that account. Only continue if you asked for this email.</p>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="stack-buttons">
          <button
            className="btn primary big block"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              void finish(verifyEmailLink(confirm.link), '/you').finally(() => setBusy(false));
            }}
          >
            {peek.account ? `Sign in as ${peek.account}` : 'Create the account'}
          </button>
          <button className="btn block" disabled={busy} onClick={() => go('/')}>
            Not me, cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page narrow center">
      {error ? (
        <>
          <h1>Couldn’t sign you in</h1>
          <p className="muted">{error}</p>
          <button className="btn primary big" onClick={() => openAuth('signup')}>
            Try again
          </button>
        </>
      ) : (
        <>
          <span className="spinner big" />
          <p className="muted">Signing you in…</p>
        </>
      )}
    </div>
  );
}
